//@flow
import Model from '../../Utils/Model/model';

import type { OrderItemObject } from './orderItemModel';
import type { AddressFields } from '../Addresses/addressModel';

type GiftMessage = {to:?string, message:?string, from:?string};

export type PaymentMethod = 'cryozonic_stripe'|'amazon'|'creditcard';

export type OrderAddress = AddressFields&{
  address_type:'billing'|'shipping',
  email:string,
};

export type OrderObject = {
  addresses:OrderAddress[],
  coupon_code:?string,
  created_at:string,
  discount_amount:number,
  id:string,
  grand_total:number,
  increment_id:string,
  order_items:OrderItemObject[],
  payment_method:PaymentMethod,
  recipient_firstname:string,
  recipient_lastname:string,
  shipping_amount:number,
  shipping_description:string,
  shipping_method:string,
  status:string,
  store_code:'membership',
  store_credit:number,
  subscribe:boolean,
  subtotal:number,
  tax_amount:number,
  tracking_numbers:string[],
  tracking_urls:string[],
  updated_at:string,
  gift_message?:GiftMessage,
  is_virtual:boolean,
};

export default class OrderModel extends Model<OrderObject> {
  getId():?string {
    return this.attributes.id || undefined;
  }

  getIncrementId():?string {
    return this.attributes.increment_id || undefined;
  }

  getStatus():?string {
    const status = this.attributes.status;

    if (typeof status !== 'string') return undefined;

    return status;
  }

  getGiftMessage():?string {
    const giftMessage = this.attributes.gift_message && this.attributes.gift_message.message;
    return giftMessage;
  }

  getShippingDescription():?string {
    const shippingDesc = this.attributes.shipping_description;

    if (typeof shippingDesc !== 'string') return undefined;

    return shippingDesc;
  }

  isVirtual():boolean {
    return this.attributes.is_virtual || false;
  }

  getShippingMethod():?string {
    const shippingMethod = this.attributes.shipping_method;
    if (typeof shippingMethod !== 'string') return undefined;

    return shippingMethod;
  }

  getStoreCredit():?number {
    return this._getFloat(this.attributes.store_credit);
  }

  getDiscountAmount():?number {
    return this._getFloat(this.attributes.discount_amount);
  }

  getGrandTotal():?number {
    return this._getFloat(this.attributes.grand_total);
  }

  getSubtotal():?number {
    return this._getFloat(this.attributes.subtotal);
  }

  getShippingAmount():?number {
    return this._getFloat(this.attributes.shipping_amount);
  }

  getTaxAmount():?number {
    return this._getFloat(this.attributes.tax_amount);
  }

  getPaymentMethod():?PaymentMethod {
    const paymentMethod = this.attributes.payment_method;
    return paymentMethod || undefined;
  }

  getCouponCode():?string {
    return this.attributes.coupon_code;
  }

  getTrackingNumbers():string[] {
    const trackingNumbers = this.attributes.tracking_numbers;

    if (!Array.isArray(trackingNumbers)) return [];

    // return a copy
    return trackingNumbers.slice();
  }

  getShipper():?string {
    const natureRates = ['naturerate_naturerate', 'checkout_checkout', 'rebill_rebill'];
    let shipper = undefined;
    const desc = this.getShippingDescription();
    const meth = this.getShippingMethod();
    if (desc) {
      if (desc.substring(0, 5).toLowerCase() === 'fedex') {
        shipper = 'fedex';
      }
      if (desc.toLowerCase() === 'landmark mercury') {
        shipper = 'canada';
      }
    }
    if (natureRates.indexOf(meth) >= 0) {
      shipper = 'naturerate';
    }

    return shipper;
  }

  _buildTrackingUrls():string[] {
    const nums = this.getTrackingNumbers();
    const urls = [];
    const shipper = this.getShipper();
    for (let i = 0; i < nums.length; i++) {
      // if tracking url is already set, it is a
      // complete url and we don't need to build it.
      if (this.attributes.tracking_urls && this.attributes.tracking_urls[i]) {
        urls.push(this.attributes.tracking_urls[i]);
      }
      else {
        switch (shipper) {
          case 'fedex':
            urls.push('http://www.fedex.com/Tracking?action=track&tracknumbers='+nums[i]);
            break;
          case 'canada':
            urls.push('https://mercury.landmarkglobal.com/tracking/track.php?trck='+nums[i]);
            break;
          case 'naturerate':
            urls.push('https://tools.usps.com/go/TrackConfirmAction.action?tLabels='+nums[i]);
            break;
        }
      }
    }

    return urls;
  }

  getTrackingUrls():string[] {
    // Build user-facing tracking URLs
    const trackingUrls = this._buildTrackingUrls();

    if (!Array.isArray(trackingUrls)) return [];

    // return a copy
    return trackingUrls.slice();
  }

  getDateCreated():Date {
    return new Date(this.attributes.created_at);
  }

  getName():?string {
    const firstname = this.attributes.recipient_firstname;
    const lastname = this.attributes.recipient_lastname;

    if (typeof firstname !== 'string') return undefined;

    return firstname + ' ' + lastname;
  }

  getAddresses():OrderAddress[] {
    const addresses = this.attributes.addresses;

    if (!Array.isArray(addresses)) return [];

    // return a copy
    return addresses.slice();
  }

  getShippingAddress():OrderAddress|Object {
    const addresses = this.attributes.addresses;
    if (!Array.isArray(addresses)) return {};

    // in case there are multiple, get a shipping addresses array
    const shipAddresses = addresses.slice().filter((address) => address.address_type == "shipping");
    if (shipAddresses.length === 0) return {};

    // return a copy
    return shipAddresses.slice()[0];
  }

  getOrderItems():OrderItemObject[] {
    const orderItems = this.attributes.order_items;

    if (!Array.isArray(orderItems)) return [];

    // return a copy
    return orderItems.slice();
  }

  getTotalItemsQty():number {
    const orderItems = this.getOrderItems();
    let totalQty = 0;
    orderItems.forEach(orderItem => totalQty += orderItem.qty_ordered || 0);
    return totalQty;
  }

  getTrackingData():Object {
    const net_sales = this.attributes.grand_total + this.attributes.store_credit;

    const data = {
      entity_id: this.attributes.id,
      status: this.attributes.status,
      shipping_description: this.attributes.shipping_description,
      shipping_method: this.attributes.shipping_method,
      subtotal: this.attributes.subtotal,
      store_credit: this.attributes.store_credit,
      discount_amount: this.attributes.discount_amount,
      shipping_amount: this.attributes.shipping_amount,
      net_sales,
      tax_amount: this.attributes.tax_amount,
      grand_total: this.attributes.grand_total,
      increment_id: this.attributes.increment_id,
      payment_method: this.attributes.payment_method,
      recipient_firstname: this.attributes.recipient_firstname,
      recipient_lastname: this.attributes.recipient_lastname,
      store_code: this.attributes.store_code,
      totalQty: this.getTotalItemsQty(),
      sku_list: this._getSkuList(),
      address: this._getShippingAddressTrackingData(),
      items: this._getQuoteItemsTrackingData(),
      coupon_code: this.attributes.coupon_code,
      subscription: this.attributes.subscribe,
    };

    return data;
  }

  _getShippingAddressTrackingData():Object|string {
    const address = this.getAddresses().find((address) => {
      return address.address_type === 'shipping';
    });

    if (!address) return '';

    return {
      region: address.region,
      postcode: address.postcode,
      city: address.city,
      country_id: address.country_id,
    };
  }

  _getQuoteItemsTrackingData():Object {
    const orderItems = this.getOrderItems();
    const data = {
      products_ids: orderItems.map(item => item.product_id).join(','),
      skus: orderItems.map(item => item.sku).join(','),
      qty_ordered: orderItems.map(item => item.qty_ordered).join(','),
      prices: orderItems.map(item => item.price).join(','),
    };

    return data;
  }

  _getSkuList():string {
    const skus = this.getOrderItems().map(item => item.sku);
    return skus.join(',');
  }
}
